import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroArchiveBox, heroArchiveBoxArrowDown, heroMagnifyingGlass, heroTrash } from '@ng-icons/heroicons/outline';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiEmptyState,
  UiFilterActions,
  UiFilterBar,
  UiFilterSearch,
  UiInput,
  UiPageHeader,
  UiPanelCard,
  UiRecordListDirective,
  UiRecordRow,
  UiRecordRowActions,
  UiRecordRowBody,
  UiRecordRowDescription,
  UiRecordRowMain,
  UiRecordRowMeta,
  UiRecordRowTitle,
  UiTextarea,
} from '../../../shared/ui';
import type { ConversationLabel } from '../../../core/api/generated/models/ConversationLabel';
import { ConversationsApiService } from '../data/conversations-api.service';
import { DEFAULT_LABEL_COLOR, LabelColorPickerComponent } from '../components/label-color-picker/label-color-picker';

/** Admin page to manage conversation label definitions (create, recolor, archive, delete). */
@Component({
  selector: 'app-conversation-labels-settings',
  imports: [
    NgIcon,
    UiBadge,
    UiButton,
    UiEmptyState,
    UiFilterActions,
    UiFilterBar,
    UiFilterSearch,
    UiInput,
    UiPageHeader,
    UiPanelCard,
    UiRecordListDirective,
    UiRecordRow,
    UiRecordRowActions,
    UiRecordRowBody,
    UiRecordRowDescription,
    UiRecordRowMain,
    UiRecordRowMeta,
    UiRecordRowTitle,
    UiTextarea,
    LabelColorPickerComponent,
  ],
  providers: [provideIcons({ heroArchiveBox, heroArchiveBoxArrowDown, heroMagnifyingGlass, heroTrash })],
  templateUrl: './labels-settings.page.html',
  styleUrl: './labels-settings.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabelsSettingsPage {
  private readonly api = inject(ConversationsApiService);
  private readonly toast = inject(ToastrService);

  protected readonly labels = signal<readonly LabelWithUsage[]>([]);
  protected readonly newName = signal('');
  protected readonly newColor = signal(DEFAULT_LABEL_COLOR);
  protected readonly newDescription = signal('');
  protected readonly search = signal('');
  protected readonly includeArchived = signal(false);
  protected readonly saving = signal(false);
  protected readonly busyLabelId = signal<string | null>(null);

  protected readonly visibleLabels = computed(() => {
    const term = this.search().trim().toLowerCase();
    if (!term) return this.labels();
    return this.labels().filter((label) => label.name.toLowerCase().includes(term));
  });

  constructor() {
    this.reload();
  }

  setName(value: string): void { this.newName.set(value); }
  setColor(value: string): void { this.newColor.set(value); }
  setDescription(value: string): void { this.newDescription.set(value); }
  setSearch(value: string): void { this.search.set(value); }
  toggleIncludeArchived(): void {
    this.includeArchived.update((value) => !value);
    this.reload();
  }

  create(): void {
    const name = this.newName().trim();
    if (name.length === 0 || this.saving()) {
      return;
    }
    this.saving.set(true);
    const description = this.newDescription().trim();
    this.api.createLabel({
      name,
      color: this.newColor(),
      ...(description ? { description } : {}),
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.newName.set('');
        this.newDescription.set('');
        this.reload();
      },
      error: (err: unknown) => {
        this.saving.set(false);
        const msg = isConflict(err) ? 'Já existe uma label com este nome.' : 'Não foi possível criar a label.';
        this.toast.error('Labels', msg);
      },
    });
  }

  toggleArchive(label: LabelWithUsage): void {
    if (this.busyLabelId() !== null) return;
    this.busyLabelId.set(label.id);
    this.api.updateLabel(label.id, { isArchived: !label.isArchived }).subscribe({
      next: () => { this.busyLabelId.set(null); this.reload(); },
      error: () => {
        this.busyLabelId.set(null);
        this.toast.error('Labels', 'Não foi possível atualizar a label.');
      },
    });
  }

  remove(label: LabelWithUsage): void {
    if (this.busyLabelId() !== null) return;
    if ((label.assignmentsCount ?? 0) > 0) {
      this.toast.error('Labels', 'Label em uso. Arquive em vez de excluir.');
      return;
    }
    if (!confirm(`Excluir definitivamente a label "${label.name}"?`)) {
      return;
    }
    this.busyLabelId.set(label.id);
    this.api.deleteLabel(label.id).subscribe({
      next: () => { this.busyLabelId.set(null); this.reload(); },
      error: (err: unknown) => {
        this.busyLabelId.set(null);
        const msg = isInUseError(err) ? 'Label em uso. Arquive em vez de excluir.' : 'Não foi possível excluir a label.';
        this.toast.error('Labels', msg);
      },
    });
  }

  private reload(): void {
    this.api.listLabels({ includeArchived: this.includeArchived(), limit: 100 }).subscribe({
      next: (response) => this.labels.set(response.data as readonly LabelWithUsage[]),
      error: () => this.toast.error('Labels', 'Não foi possível carregar as labels.'),
    });
  }
}

/** Local extension of {@link ConversationLabel} that includes server-computed fields not yet
 * in the generated client (regenerated on next `npm run api:generate`). */
interface LabelWithUsage extends ConversationLabel {
  description?: string | null;
  assignmentsCount?: number;
}

/** True when the API rejected a delete because the label still has assignments. */
function isInUseError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const code = (error as { error?: { code?: string } }).error?.code;
  return code === 'conversation_label_in_use';
}

/** True when the API rejected an insert because another label already uses the name. */
function isConflict(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  return (error as { status?: number }).status === 409;
}
